import { isEmpty } from 'lodash';
import { boostCommissionIncentiveTypes, INCENTIVES } from '../../sagas/campaigns/editFlow/reducers';
import { convertJSONToFormData, dataURIToBlob, localeFormatCurrency, localeFormatNew } from '../../utils/custom';

const prepareIncentives = (incentive_type, incentives) => {
  const initIncentives = { bonuses: [], gmv_modifiers: [], commission_rate_multipliers: [], minimum_commission_rates: [] };

  switch (incentive_type) {
    case INCENTIVES.LINKS: {
      return { ...initIncentives, bonuses: [incentives.links_incentive] };
    }
    case INCENTIVES.GMV: {
      return  { ...initIncentives, gmv_modifiers: incentives.gmv_incentive };
    }
    case INCENTIVES.BOOST_COMMISSION: {
      switch (incentives.boost_commission_incentive.boost_type) {
        case boostCommissionIncentiveTypes.COMMISSION_RATE_MULTIPLIER: {
          return { ...initIncentives, commission_rate_multipliers: [{ multiplier: localeFormatNew({ value: incentives.boost_commission_incentive.multiplier_value, maxDigits: 2 }) }] };
        }
        case boostCommissionIncentiveTypes.MINIMUM_COMMISSION_RATE: {
          return { ...initIncentives, minimum_commission_rates: [{ minimum_rate_percentage: incentives.boost_commission_incentive.minimum_rate_value }] };
        }
      }
    } break;
    default: return {};
  }
};

const hasFilledValue = value => value !== '' && value !== null && value !== undefined;

export const hasCompleteCampaignIncentive = data => {
  switch (data.incentive_type) {
    case INCENTIVES.LINKS:
      return [
        data.links_incentive.reward,
        data.links_incentive.qualified_links_required,
        data.links_incentive.minimum_clicks,
        data.links_incentive.minimum_gmv
      ].every(hasFilledValue);
    case INCENTIVES.GMV:
      return !isEmpty(data.gmv_incentive)
        && data.gmv_incentive.every(tier => hasFilledValue(tier.minimum_gmv) && hasFilledValue(tier.reward));
    case INCENTIVES.BOOST_COMMISSION:
      switch (data.boost_commission_incentive.boost_type) {
        case boostCommissionIncentiveTypes.COMMISSION_RATE_MULTIPLIER:
          return hasFilledValue(data.boost_commission_incentive.boost_type)
            && hasFilledValue(data.boost_commission_incentive.multiplier_value)
            && hasFilledValue(data.boost_commission_incentive.boosted_rate);
        case boostCommissionIncentiveTypes.MINIMUM_COMMISSION_RATE:
          return hasFilledValue(data.boost_commission_incentive.boost_type)
            && hasFilledValue(data.boost_commission_incentive.minimum_rate_value)
            && hasFilledValue(data.boost_commission_incentive.boosted_rate);
        default:
          return false;
      }
    default:
      return false;
  }
};

export const canLaunchCampaign = data => Boolean(
  data.name?.trim()
  && !isEmpty(data.users)
  && hasCompleteCampaignIncentive(data)
);

const getCampaignRequestBody = (data, status) => ({
  name: data.name,
  ...(data.description && { description: data.description }),
  ...(data.date_start && data.date_start !== 'Invalid date' ? { date_start: data.date_start } : {}),
  ...(data.date_start_tz ? { date_start_tz: data.date_start_tz } : {}),
  ...(data.date_end && data.date_end !== 'Invalid date' ? { date_end: data.date_end } : {}),
  ...(data.date_end_tz ? { date_end_tz: data.date_end_tz } : {}),
  user_group_join_type: 'include',
  ...(status && { status }),
  ...prepareIncentives(data.incentive_type, {
    links_incentive: data.links_incentive,
    gmv_incentive: data.gmv_incentive,
    boost_commission_incentive: data.boost_commission_incentive
  }),
  users: data.users.map(x => ({ uuid: x.uuid }))
});

export const prepareCampaignData = (data, status) => {
  const body = getCampaignRequestBody(data, status);

  const formData = convertJSONToFormData(body);
  data.image_url && !data.image_url.includes('http') && formData.append('image', dataURIToBlob(data.image_url), 'image.png');

  return formData;
};

export const prepareCampaignBalanceData = data => convertJSONToFormData(getCampaignRequestBody(data));

export const generateCampaignDefaultName = data => {
  const { data: { users, incentive_type, links_incentive, gmv_incentive, boost_commission_incentive } } = data;
  const usersPart = isEmpty(users) ? '' : ( users.length > 1 ? `${users.length} users` : users[0].name);

  switch (incentive_type) {
    case INCENTIVES.LINKS: {

      return `${usersPart} - Links bonus: ${links_incentive.reward ? localeFormatCurrency({ value: links_incentive.reward }) : ''}`;
    }
    case INCENTIVES.GMV: {
      const gmvReward = gmv_incentive.reduce((total, item) => {
        const reward = parseFloat(item.reward) || 0;
        return total + reward;
      }, 0);

      return `${usersPart} - Sales bonus: ${gmvReward ? localeFormatCurrency({ value: gmvReward }) : ''}`;
    }
    case INCENTIVES.BOOST_COMMISSION: {

      switch (boost_commission_incentive.boost_type) {
        case boostCommissionIncentiveTypes.COMMISSION_RATE_MULTIPLIER: return `${usersPart} - Rate: ${localeFormatNew({ value: boost_commission_incentive.multiplier_value, maxDigits: 2 })}x`;
        case boostCommissionIncentiveTypes.MINIMUM_COMMISSION_RATE: return `${usersPart} - Rate: ${boost_commission_incentive.minimum_rate_value }%`;
      }

      return `${usersPart} - Rate:`;
    }
    default: return `${usersPart}`;
  }
};